import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Search, Users, Mail, Upload, FileCheck2, ShieldCheck, ShieldOff, RefreshCw, ArrowLeft, Link as LinkIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { NotificationBell } from "../components/site/NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNT_BADGE = {
  active: "bg-[#C7F0D8] text-[#0f6b3d]",
  invited: "bg-[#FFE8B0] text-[#7a5b00]",
  suspended: "bg-[#FBD5D5] text-[#9b1c1c]",
};
const ACCOUNT_LABEL = { active: "Active", invited: "Invited", suspended: "Suspended" };
const APPROVAL_LABEL = { approved: "Approved", pending_approval: "Pending", suspended: "Suspended" };

const EMPTY_FORM = {
  legal_name: "", dba_name: "", contact_name: "", email: "", phone: "", address: "",
  services: "", service_areas: "", license_number: "", insurance_carrier: "",
  insurance_policy: "", insurance_expiry: "", referral_percentage: "", internal_notes: "",
};

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function AdminContractors() {
  const { token, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && !token) navigate("/admin/login");
    if (!authLoading && token && role && role !== "admin") navigate(role === "agent" ? "/agent" : "/portal");
  }, [authLoading, token, role, navigate]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/contractors`, authHeader);
      setContractors(data.contractors || []);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); navigate("/admin/login"); return; }
      toast.error("Failed to load contractors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contractors;
    return contractors.filter((c) =>
      [c.legal_name, c.dba_name, c.contact_name, c.email, c.contractor_ref, (c.service_areas || []).join(" "), (c.services || []).join(" ")]
        .filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [contractors, query]);

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.legal_name || !form.contact_name || !form.email) {
      toast.error("Legal name, contact name and email are required."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
        referral_percentage: form.referral_percentage === "" ? null : Number(form.referral_percentage),
      };
      const { data } = await axios.post(`${API}/admin/contractors`, payload, authHeader);
      if (data.invite_emailed) {
        toast.success("Contractor added and invite emailed.");
      } else {
        const copied = await copyToClipboard(data.invite_link);
        toast.warning(copied
          ? "Contractor added. Invite email couldn't be sent — the invite link was copied to your clipboard to share manually."
          : "Contractor added, but the invite email couldn't be sent. Open the contractor and use 'Copy invite link' to share it.");
      }
      setAddOpen(false); setForm(EMPTY_FORM); load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add contractor.");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="admin-contractors">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button data-testid="nav-project-requests" onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-[#133955] hover:bg-[#EAF7FC] transition-colors">
              <ArrowLeft size={16} /> Project Requests
            </button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#133955] tracking-tight">Contractors</h1>
            <p className="text-[#173042]/70 mt-1" data-testid="contractors-count">{filtered.length} of {contractors.length} contractor{contractors.length === 1 ? "" : "s"}</p>
          </div>
          <button data-testid="add-contractor-btn" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors">
            <Plus size={18} /> Add Contractor
          </button>
        </div>

        <div className="relative mb-6 max-w-md">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#173042]/40" />
          <input data-testid="contractors-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search contractors…" className="hpc-input bg-white" style={{ paddingLeft: "2.75rem" }} />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)]" data-testid="no-contractors">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#EAF7FC] text-[#54BCE6] mx-auto mb-4"><Users size={26} /></span>
            <p className="text-[#173042] font-semibold">No contractors yet</p>
            <p className="text-[#173042]/60 text-sm mt-1">Add your first contractor to start assigning projects.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="contractors-list">
            {filtered.map((c) => (
              <button key={c.id} data-testid={`contractor-row-${c.id}`} onClick={() => setDetail(c)}
                className="w-full text-left rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-5 hover:border-[#54BCE6] transition-colors">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-[#54BCE6]">{c.contractor_ref}</span>
                    <span className="font-bold text-[#133955]">{c.dba_name || c.legal_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${ACCOUNT_BADGE[c.account_status] || ACCOUNT_BADGE.invited}`} data-testid={`contractor-account-${c.id}`}>{ACCOUNT_LABEL[c.account_status]}</span>
                    <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#EAF7FC] text-[#133955]" data-testid={`contractor-approval-${c.id}`}>{APPROVAL_LABEL[c.approval_status]}</span>
                    {c.agreement && <FileCheck2 size={16} className="text-[#0f6b3d]" title="Agreement on file" />}
                  </div>
                </div>
                <div className="mt-2 text-sm text-[#173042]/70 flex flex-wrap gap-x-4 gap-y-1">
                  <span>{c.contact_name}</span><span className="break-all">{c.email}</span>
                  {c.service_areas?.length > 0 && <span>{c.service_areas.join(", ")}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Add contractor modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#133955]">Add Contractor</DialogTitle>
            <DialogDescription>An email invite will be sent so they can set their own password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} data-testid="add-contractor-form" className="grid sm:grid-cols-2 gap-4">
            <Field label="Legal Name *" htmlFor="cf-legal-name"><input id="cf-legal-name" data-testid="cf-legal-name" className="hpc-input" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></Field>
            <Field label="DBA Name" htmlFor="cf-dba-name"><input id="cf-dba-name" data-testid="cf-dba-name" className="hpc-input" value={form.dba_name} onChange={(e) => setForm({ ...form, dba_name: e.target.value })} /></Field>
            <Field label="Contact Name *" htmlFor="cf-contact-name"><input id="cf-contact-name" data-testid="cf-contact-name" className="hpc-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
            <Field label="Email *" htmlFor="cf-email"><input id="cf-email" data-testid="cf-email" type="email" className="hpc-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone" htmlFor="cf-phone"><input id="cf-phone" data-testid="cf-phone" className="hpc-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Address" htmlFor="cf-address"><input id="cf-address" data-testid="cf-address" className="hpc-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Services (comma separated)" htmlFor="cf-services"><input id="cf-services" data-testid="cf-services" className="hpc-input" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder="Kitchen Remodel, Home Additions" /></Field>
            <Field label="Service Areas (comma separated)" htmlFor="cf-service-areas"><input id="cf-service-areas" data-testid="cf-service-areas" className="hpc-input" value={form.service_areas} onChange={(e) => setForm({ ...form, service_areas: e.target.value })} placeholder="Charleston, Summerville" /></Field>
            <Field label="License #" htmlFor="cf-license"><input id="cf-license" data-testid="cf-license" className="hpc-input" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} /></Field>
            <Field label="Insurance Carrier" htmlFor="cf-ins-carrier"><input id="cf-ins-carrier" data-testid="cf-ins-carrier" className="hpc-input" value={form.insurance_carrier} onChange={(e) => setForm({ ...form, insurance_carrier: e.target.value })} /></Field>
            <Field label="Insurance Policy #" htmlFor="cf-ins-policy"><input id="cf-ins-policy" data-testid="cf-ins-policy" className="hpc-input" value={form.insurance_policy} onChange={(e) => setForm({ ...form, insurance_policy: e.target.value })} /></Field>
            <Field label="Insurance Expiry" htmlFor="cf-ins-expiry"><input id="cf-ins-expiry" data-testid="cf-ins-expiry" type="date" className="hpc-input" value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} /></Field>
            <Field label="Agreed Referral %" htmlFor="cf-referral-pct"><input id="cf-referral-pct" data-testid="cf-referral-pct" type="number" min="0" max="100" step="0.1" className="hpc-input" value={form.referral_percentage} onChange={(e) => setForm({ ...form, referral_percentage: e.target.value })} placeholder="e.g. 12.5" /></Field>
            <div className="sm:col-span-2">
              <Field label="Internal Notes (admin only)" htmlFor="cf-notes"><textarea id="cf-notes" data-testid="cf-notes" className="hpc-input min-h-[80px]" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} /></Field>
            </div>
            <DialogFooter className="sm:col-span-2">
              <button type="button" onClick={() => setAddOpen(false)} className="rounded-full px-6 py-3 font-bold text-[#133955] hover:bg-[#EAF7FC] transition-colors">Cancel</button>
              <button type="submit" data-testid="cf-submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">
                {saving && <Loader2 size={16} className="animate-spin" />} Add & Send Invite
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <ContractorDetail key={detail?.id} contractor={detail} onClose={() => setDetail(null)} authHeader={authHeader} token={token} onChange={load} />
    </div>
  );
}

function ContractorDetail({ contractor, onClose, authHeader, token, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(contractor?.referral_percentage ?? "");
  const [isRE, setIsRE] = useState(!!contractor?.is_real_estate);
  if (!contractor) return null;
  const c = contractor;

  const patch = async (updates, msg) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/contractors/${c.id}`, updates, authHeader);
      toast.success(msg); onChange(); onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed.");
    } finally { setBusy(false); }
  };

  const savePct = async () => {
    const val = pct === "" ? null : Number(pct);
    if (val !== null && (isNaN(val) || val < 0 || val > 100)) { toast.error("Referral % must be between 0 and 100."); return; }
    await patch({ referral_percentage: val }, "Referral percentage saved.");
  };

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/contractors/${c.id}/resend-invite`, {}, authHeader);
      toast.success(data.invite_emailed ? "Invite re-sent." : "Invite email failed to send.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not resend invite.");
    } finally { setBusy(false); }
  };

  const copyInviteLink = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/contractors/${c.id}/resend-invite`, {}, authHeader);
      const copied = await copyToClipboard(data.invite_link);
      toast.success(copied ? "Fresh invite link copied to clipboard." : "Invite link generated — check the email or logs.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not generate invite link.");
    } finally { setBusy(false); }
  };

  const uploadAgreement = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/contractors/${c.id}/agreement`, fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      toast.success("Agreement uploaded."); onChange(); onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed.");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={!!contractor} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="contractor-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-[#133955] flex items-center gap-2">
            <span className="font-mono text-[#54BCE6]">{c.contractor_ref}</span> {c.dba_name || c.legal_name}
          </DialogTitle>
          <DialogDescription>
            Account: <strong>{ACCOUNT_LABEL[c.account_status]}</strong> · Approval: <strong>{APPROVAL_LABEL[c.approval_status]}</strong>
          </DialogDescription>
        </DialogHeader>

        <button data-testid="view-contractor-profile" onClick={() => navigate(`/admin/contractors/${c.id}`)}
          className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors">
          View Full Profile — Projects & Repairs
        </button>

        <dl className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div><dt className="text-[#173042]/50 text-xs font-semibold uppercase">Contact</dt><dd className="text-[#173042]">{c.contact_name}</dd></div>
          <div><dt className="text-[#173042]/50 text-xs font-semibold uppercase">Email</dt><dd className="text-[#173042] break-all">{c.email}</dd></div>
          {c.phone && <div><dt className="text-[#173042]/50 text-xs font-semibold uppercase">Phone</dt><dd className="text-[#173042]">{c.phone}</dd></div>}
          {c.license_number && <div><dt className="text-[#173042]/50 text-xs font-semibold uppercase">License</dt><dd className="text-[#173042]">{c.license_number}</dd></div>}
          {c.services?.length > 0 && <div className="col-span-2"><dt className="text-[#173042]/50 text-xs font-semibold uppercase">Services</dt><dd className="text-[#173042]">{c.services.join(", ")}</dd></div>}
          {c.service_areas?.length > 0 && <div className="col-span-2"><dt className="text-[#173042]/50 text-xs font-semibold uppercase">Service Areas</dt><dd className="text-[#173042]">{c.service_areas.join(", ")}</dd></div>}
          {c.internal_notes && <div className="col-span-2"><dt className="text-[#173042]/50 text-xs font-semibold uppercase">Internal Notes</dt><dd className="text-[#173042]">{c.internal_notes}</dd></div>}
        </dl>

        <div className="rounded-xl bg-[#EAF7FC] p-4 mb-2" data-testid="referral-pct-block">
          <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Agreed Referral %</p>
          <div className="flex items-center gap-2">
            <input
              data-testid="referral-pct-input"
              type="number" min="0" max="100" step="0.1"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              placeholder="Not set"
              className="hpc-input bg-white max-w-[140px]"
            />
            <span className="text-[#133955] font-bold">%</span>
            <button data-testid="referral-pct-save" onClick={savePct} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">
              Save %
            </button>
          </div>
          <p className="text-xs text-[#173042]/60 mt-2">Used to calculate the referral fee on this contractor's contracts.</p>
        </div>

        <label className="flex items-center gap-3 rounded-xl bg-[#EAF7FC] p-4 mb-2 cursor-pointer" data-testid="re-flag-block">
          <input type="checkbox" data-testid="re-flag-toggle" checked={isRE}
            onChange={async (e) => {
              const val = e.target.checked; setIsRE(val); setBusy(true);
              try { await axios.patch(`${API}/admin/contractors/${c.id}`, { is_real_estate: val }, authHeader); toast.success(val ? "Marked as Real Estate Contractor." : "Removed Real Estate flag."); onChange(); }
              catch { toast.error("Update failed."); setIsRE(!val); }
              finally { setBusy(false); }
            }} />
          <span className="text-sm font-bold text-[#133955]">Real Estate Contractor <span className="font-normal text-[#173042]/60">— suggested first when assigning agent repair requests</span></span>
        </label>

        <div className="rounded-xl bg-[#EAF7FC] p-4 mb-2">
          <p className="text-xs font-bold text-[#133955] uppercase tracking-wide mb-2">Agreement</p>
          {c.agreement ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <a data-testid="view-agreement" href={`${API}/files/${c.agreement.file_id}?auth=${token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#133955] hover:underline">
                <FileCheck2 size={16} className="text-[#0f6b3d]" /> {c.agreement.original_filename}
              </a>
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#133955] cursor-pointer hover:underline">
                <Upload size={15} /> Replace<input type="file" className="hidden" onChange={uploadAgreement} data-testid="upload-agreement" />
              </label>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#133955] cursor-pointer hover:underline" data-testid="agreement-upload-label">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload signed agreement
              <input type="file" className="hidden" onChange={uploadAgreement} data-testid="upload-agreement" />
            </label>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {c.account_status !== "active" && (
            <button data-testid="resend-invite" onClick={resend} disabled={busy} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors disabled:opacity-60">
              <RefreshCw size={15} /> Resend Invite
            </button>
          )}
          {c.account_status !== "active" && (
            <button data-testid="copy-invite-link" onClick={copyInviteLink} disabled={busy} className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.2)] text-[#133955] px-5 py-2.5 text-sm font-bold hover:border-[#54BCE6] transition-colors disabled:opacity-60">
              <LinkIcon size={15} /> Copy Invite Link
            </button>
          )}
          {c.approval_status !== "approved" && (
            <button data-testid="approve-contractor" onClick={() => patch({ approval_status: "approved" }, "Contractor approved.")} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#0f6b3d] text-white px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
              <ShieldCheck size={15} /> Approve
            </button>
          )}
          {c.approval_status !== "suspended" ? (
            <button data-testid="suspend-contractor" onClick={() => patch({ approval_status: "suspended" }, "Contractor suspended.")} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#9b1c1c] text-white px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
              <ShieldOff size={15} /> Suspend
            </button>
          ) : (
            <button data-testid="reactivate-contractor" onClick={() => patch({ approval_status: "approved" }, "Contractor reactivated.")} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#0f6b3d] text-white px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
              <ShieldCheck size={15} /> Reactivate
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
